import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { v4 as uuidV4 } from 'uuid';
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  StateCondition,
  HubOptionStateValue,
  ConditionQuantifier,
  HUB_MAX_STATE_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../context/NodeEditContext';
import { OptionSectionRow } from '../shared/OptionSectionRow';
import { createEmptySubCondition } from '../stateConditionDefaults';
import { StateConditionCard } from './StateConditionCard';
import { isStateConditionValid } from './stateConditionValidation';
import {
  HUB_CONTINUE_DISABLED_TEXT_MAX_LENGTH,
  HUB_DISABLED_TEXT_MAX_LENGTH,
} from '../hubStepDefaults';
import './HubOptionState.scss';

interface HubOptionStateProps {
  /** Unique ID for DndContext isolation (e.g. option UUID) */
  optionUuid: string;
  /** The full state object from HubOption.state */
  optionState: { state: HubOptionStateValue; text?: string };
  stateConditions: StateCondition[];
  isDisabled: boolean;
  isContinue: boolean;
  onChange: (changes: {
    state?: { state: HubOptionStateValue; text?: string };
    stateConditions?: StateCondition[];
  }) => void;
}

const STATE_OPTIONS: { value: HubOptionStateValue; label: string }[] = [
  { value: HubOptionStateValue.Active, label: 'Active' },
  { value: HubOptionStateValue.Hidden, label: 'Hidden' },
  { value: HubOptionStateValue.Disabled, label: 'Disabled' },
];

/**
 * Hook: all state condition CRUD logic.
 * Inline within same file per spec (NOT a separate file).
 */
function useStateConditions(
  stateConditions: StateCondition[],
  onChange: HubOptionStateProps['onChange'],
) {
  const addCondition = useCallback(() => {
    const newCondition: StateCondition = {
      uuid: uuidV4(),
      quantifier: ConditionQuantifier.All,
      subConditions: [createEmptySubCondition()],
      resultState: HubOptionStateValue.Hidden,
    };
    onChange({ stateConditions: [...stateConditions, newCondition] });
  }, [stateConditions, onChange]);

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({
        stateConditions: stateConditions.filter((c) => c.uuid !== uuid),
      });
    },
    [stateConditions, onChange],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: StateCondition) => {
      onChange({
        stateConditions: stateConditions.map((c) =>
          c.uuid === uuid ? updated : c,
        ),
      });
    },
    [stateConditions, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = stateConditions.findIndex((c) => c.uuid === active.id);
        const newIndex = stateConditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ stateConditions: arrayMove(stateConditions, oldIndex, newIndex) });
        }
      }
    },
    [stateConditions, onChange],
  );

  /** Summary: count sub-conditions per resultState, e.g. "HIDDEN (3), DISABLED (2)" */
  const summary = useMemo((): string => {
    if (stateConditions.length === 0) return '';

    const subCounts: Partial<Record<HubOptionStateValue, number>> = {};
    for (const c of stateConditions) {
      subCounts[c.resultState] = (subCounts[c.resultState] ?? 0) + c.subConditions.length;
    }

    return Object.entries(subCounts)
      .map(([state, count]) => `${state.toUpperCase()} (${count})`)
      .join(', ');
  }, [stateConditions]);

  return {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  };
}

/** Stable sensor config — defined at module level to avoid re-creation on every render */
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

export function HubOptionState({
  optionUuid,
  optionState,
  stateConditions,
  isDisabled,
  isContinue,
  onChange,
}: HubOptionStateProps) {
  const disabledTextMaxLength = isContinue
    ? HUB_CONTINUE_DISABLED_TEXT_MAX_LENGTH
    : HUB_DISABLED_TEXT_MAX_LENGTH;
  const disabledTextLength = optionState.text?.length ?? 0;
  const defaultState = optionState.state;
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const memoryBankSlots = (ctx?.memoryBankSlots as MemoryDTO[] | undefined) ?? [];

  const numberVariables = useMemo(
    () => memoryBankSlots.filter((v: MemoryDTO) => v.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  } = useStateConditions(stateConditions, onChange);

  const [isOverridesExpanded, setIsOverridesExpanded] = useState(false);

  const hasOverrides = stateConditions.length > 0;

  /** isConfigured = has overrides AND all sub-conditions have fields filled (same logic as Text & Dest / Icon) */
  const isConfigured = useMemo(() => hasOverrides && stateConditions.every((c) =>
    c.subConditions.length > 0 && c.subConditions.every((sub) => {
      if (sub.variableId == null) return false;
      const hasInt = sub.intValue !== undefined && sub.intValue !== '';
      const hasVar = sub.varValue !== undefined && sub.varValue !== null && sub.varValue !== 0;
      return hasInt || hasVar;
    }),
  ), [hasOverrides, stateConditions]);
  const canAdd = stateConditions.length < HUB_MAX_STATE_CONDITIONS;

  const sortableItems = useMemo(
    () => stateConditions.map((c) => c.uuid),
    [stateConditions],
  );

  const handleDefaultStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newState = e.target.value as HubOptionStateValue;
      const updated: { state: HubOptionStateValue; text?: string } = { state: newState };
      if (newState === HubOptionStateValue.Disabled) {
        updated.text = optionState.text ?? '';
      }
      onChange({ state: updated });
    },
    [onChange, optionState],
  );

  const handleDefaultTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ state: { ...optionState, text: e.target.value } });
    },
    [onChange, optionState],
  );

  const stateSelect = (
    <div className="hub-option-state__default-controls">
      <Form.Control
        as="select"
        size="sm"
        className="hub-option-state__default-select"
        value={defaultState}
        onChange={handleDefaultStateChange}
        disabled={isDisabled}
        data-testid="default-state-select"
      >
        {STATE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Control>
      {defaultState === HubOptionStateValue.Disabled && (
        <div className="hub-option-state__default-text-wrap">
          <Form.Control
            size="sm"
            type="text"
            className="hub-option-state__default-text"
            placeholder="Explanation text"
            value={optionState.text ?? ''}
            onChange={handleDefaultTextChange}
            disabled={isDisabled}
            maxLength={disabledTextMaxLength}
            data-testid="default-state-text"
          />
          <Form.Text className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
            {disabledTextMaxLength - disabledTextLength}
          </Form.Text>
        </div>
      )}
    </div>
  );

  return (
    <OptionSectionRow
      label="State"
      inlineControl={stateSelect}
      summary={summary || undefined}
      isConfigured={isConfigured}
      showDot={hasOverrides}
      hasError={hasOverrides && !isConfigured}
      expandLabel="Overrides"
      isExpanded={isOverridesExpanded}
      onExpandToggle={() => setIsOverridesExpanded((prev) => !prev)}
    >
      <div className="hub-option-state" data-testid="hub-option-state">
        <p className="hub-option-state__helper">
          First match sets state. Evaluated top-to-bottom.
        </p>

        <DndContext
          id={`state-conditions-${optionUuid}`}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {stateConditions.map((cond) => (
              <StateConditionCard
                key={cond.uuid}
                condition={cond}
                memoryVariables={numberVariables}
                isDisabled={isDisabled}
                isContinue={isContinue}
                isValid={isStateConditionValid(cond)}
                onChange={(updated) => updateCondition(cond.uuid, updated)}
                onRemove={() => removeCondition(cond.uuid)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Otherwise row — pinned last, not editable */}
        <div className="hub-option-state__otherwise" data-testid="otherwise-row">
          <span className="hub-option-state__otherwise-label">Otherwise</span>
          <span className="hub-option-state__otherwise-text">
            uses default state above
          </span>
        </div>

        {/* Add button + counter */}
        <div className="hub-option-state__footer">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={addCondition}
            disabled={isDisabled || !canAdd}
            data-testid="add-state-condition"
          >
            + Add State Override
          </button>
          <span className="hub-option-state__counter" data-testid="condition-counter">
            {stateConditions.length} / {HUB_MAX_STATE_CONDITIONS}
          </span>
        </div>
      </div>
    </OptionSectionRow>
  );
}
